if (typeof debounce !== "function") {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.reset = this.querySelector('button[type="reset"]');
    if (this.querySelector("[data-predictive-search]")) {
      this.predictiveSearchResults = this.querySelector(
        "[data-predictive-search]"
      ).querySelector(".predictive-search__results");
    }
    this.isOpen = false;
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form = this.querySelector("form.search");
    this.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    if (this.reset) {
      this.reset.addEventListener("click", () => {
        this.close(true);
      });
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown" || event.code === "Enter") {
      event.preventDefault();
      if (event.code === "Enter") {
        this.form.submit();
      }
    }
  }

  resultsLoaded() {
    return this.querySelector('.predictive-search__results-list') && !this.getAttribute("loading");
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[limit]=12&resources[limit_scope]=each&resources[options][fields]=title,body,product_type,variants.title,variants.sku`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    document.body.classList.add("shadow--body", "search-active");

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });

    document.body.classList.add('overflow-hidden');
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = "";

        if (this.reset) this.reset.classList.add("hidden");
      }

      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", false);
    }

    this.removeAttribute("open");
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute("style");
    }

    document.body.classList.remove("shadow--body", "search-active");

    document.body.removeEventListener("click", this.onBodyClick, {
      passive: true,
    });

    this.isOpen = false;

    document.body.classList.remove('overflow-hidden');
  }

  //Body click event to close drawer
  handleBodyClick(evt) {
    const target = evt.target;    
    const clickInside = target.closest(".js-predictive-search-container");
     
    if (clickInside) return;

    this.close(true);
  }
}

customElements.define("predictive-search", PredictiveSearch);
